'use client'

import { useEffect, useRef, useState } from 'react'
import { MapPin, ExternalLink, Loader2 } from 'lucide-react'
import dynamic from 'next/dynamic'

const OFFICE_LOCATION = {
  lat: 20.287806,
  lng: 85.845337,
}

const OFFICE_ADDRESS = 'Plot no 613 near Reliance fresh, in front of 5star electronics, Saheed Nagar, Bhubaneswar, Odisha 751007'

// Lazy load the actual map component
const LazyMapComponent = dynamic(() => import('./office-map-client'), {
  loading: () => (
    <div className="rounded-2xl overflow-hidden border border-slate-300 shadow-lg h-96 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#003DA5] mx-auto mb-4 animate-spin" />
        <p className="text-muted-foreground">Loading map...</p>
      </div>
    </div>
  ),
  ssr: false, // Disable SSR for Leaflet
})

export function OfficeLocationMap() {
  const [isInView, setIsInView] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Intersection Observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true)
            observer.disconnect()
          }
        })
      },
      {
        rootMargin: '100px', // Load 100px before it comes into view
      }
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef}>
      {isInView ? (
        <LazyMapComponent
          location={OFFICE_LOCATION}
          address={OFFICE_ADDRESS}
        />
      ) : (
        // Placeholder with static preview
        <div className="rounded-2xl overflow-hidden border border-slate-300 shadow-lg h-96 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
          <div className="text-center px-4">
            <MapPin size={48} className="text-[#003DA5] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">Our Office Location</h3>
            <p className="text-muted-foreground max-w-md mb-4 text-sm">
              {OFFICE_ADDRESS}
            </p>
            <a
              href={`https://www.google.com/maps?q=${OFFICE_LOCATION.lat},${OFFICE_LOCATION.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#003DA5] hover:text-[#FF7A00] font-medium transition-colors"
            >
              View on Google Maps
              <ExternalLink size={16} />
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
